import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getPublicCertification } from "./services/certificationApi";
import { formatEnglishDate, formatEnglishDateRange } from "./utils/dateFormatting";
import LevelBadge from "./components/certification/LevelBadge";
import BadgesGrid from "./components/certification/BadgesGrid";
import EvidenceList from "./components/certification/EvidenceList";
import IPFSLinks from "./components/certification/IPFSLinks";
import CertificationQRCode from "./components/certification/CertificationQRCode";
import "./components/certification/certification.css";
import logo from "./LogoAppleTreeHD.svg";
import { FaXTwitter, FaLinkedin, FaFacebook, FaEnvelope, FaWhatsapp, FaLink, FaShareNodes } from "react-icons/fa6";
import { resolveCompanyLogoUrl } from "./utils/companyLogo";

const statusStyles = {
  APPROVED: { background: "#dcfce7", color: "#166534", label: "APPROVED" },
  PENDING: { background: "#fef3c7", color: "#92400e", label: "PENDING" },
  EXPIRED: { background: "#fee2e2", color: "#991b1b", label: "EXPIRED" },
  REVOKED: { background: "#fecaca", color: "#7f1d1d", label: "REVOKED" },
};

const PublicCertificationPage = () => {
  const { certificationId } = useParams();
  const [certification, setCertification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadCertification = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getPublicCertification(certificationId);
        if (isMounted) {
          setCertification(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Errore durante il caricamento");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (certificationId) {
      loadCertification();
    }

    return () => {
      isMounted = false;
    };
  }, [certificationId]);

  const isExpired = useMemo(() => {
    if (!certification?.expiresAt) return false;
    return new Date(certification.expiresAt) < new Date();
  }, [certification]);

  const statusConfig = statusStyles[certification?.status] || statusStyles.APPROVED;
  const companyName = certification?.entityName || certification?.companyName || "Certified Company";
  const companyLogoUrl = useMemo(() => resolveCompanyLogoUrl(certification), [certification]);

  const verifiedEvidence = useMemo(() => {
    const items = Array.isArray(certification?.evidence) ? certification.evidence : [];
    return items.filter((item) => {
      if (item?.isVerified === true) return true;
      if (item?.verificationStatus === "VERIFIED") return true;
      // Legacy payload fallback: public endpoint historically exposed verified-only evidence.
      return item?.isVerified == null && item?.verificationStatus == null;
    });
  }, [certification]);

  const verifiedEvidenceCount = useMemo(() => {
    const summaryCount = certification?.evidenceSummary?.verified;
    const parsedSummaryCount = Number(summaryCount);
    return Number.isFinite(parsedSummaryCount) ? parsedSummaryCount : verifiedEvidence.length;
  }, [certification, verifiedEvidence]);

  useEffect(() => {
    if (!certification) return;
    const title = `Certificazione ESG ${certification.level || ""} - ${companyName}`.trim();
    document.title = title;

    const description = `Verified ESG certification with ${certification.badges?.length || 0} badges and ${certification.evidence?.length || 0} evidences.`;

    const setMeta = (name, content) => {
      let tag = document.querySelector(`meta[name='${name}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    const setOg = (property, content) => {
      let tag = document.querySelector(`meta[property='${property}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", description);
    setOg("og:title", title);
    setOg("og:description", description);
  }, [certification, companyName]);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>Loading certification...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h2 style={styles.title}>Certification not found</h2>
          <p style={styles.subtitle}>{error}</p>
          <p style={{marginTop: "16px", fontSize: "0.9rem", color: "#666"}}>
            If this certification was just created, please wait a moment and refresh the page. 
            For off-chain certifications, it may take a few seconds to become available.
          </p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              marginTop: "16px",
              padding: "10px 20px",
              background: "#3B82F6",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "0.9rem"
            }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* AppleTree Branding Header */}
        <header style={styles.appleTreeHeader}>
          <img src={logo} alt="AppleTree" style={styles.appleTreeLogo} />
          <span style={styles.appleTreeText}>ESG Certification Platform</span>
        </header>

        {/* Certified Company Section */}
        <section style={styles.companySection}>
          <div style={styles.companyLogoBox}>
            {companyLogoUrl ? (
              <img src={companyLogoUrl} alt={companyName} style={styles.companyLogo} />
            ) : (
              <div style={styles.companyLogoPlaceholder}>{companyName.charAt(0).toUpperCase()}</div>
            )}
          </div>
          <div style={styles.companyInfo}>
            <h1 style={styles.companyName}>{companyName}</h1>
            <p style={styles.companySubtitle}>Verified ESG Certification</p>
          </div>
        </section>

        {(certification?.status === "REVOKED" || certification?.status === "EXPIRED" || isExpired) && (
          <div
            className={`cert-banner ${certification?.status === "REVOKED" ? "cert-banner--danger" : "cert-banner--warning"}`}
          >
            {certification?.status === "REVOKED"
              ? "Certification revoked"
              : `Certification expired on ${formatEnglishDate(certification?.expiresAt)}`}
          </div>
        )}  

        <section style={styles.hero}>
          <LevelBadge level={certification?.level || "NONE"} size="large" showLabel />
          <div>
            <div style={styles.heroTitle}>{certification?.level || "NONE"} CERTIFICATION</div>
            <span style={{ ...styles.statusBadge, background: statusConfig.background, color: statusConfig.color }}>
              {statusConfig.label}
            </span>
            <div style={styles.heroDates}>
              Valid from {formatEnglishDateRange(certification?.issuedAt, certification?.expiresAt)}
            </div>
          </div>
        </section>

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Earned Badges</h2>
            <span style={styles.sectionCount}>{certification?.badges?.length || 0} earned badges</span>
          </div>
          <BadgesGrid badges={certification?.badges} showDescription />
        </section>

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Verified Evidence</h2>
            <span style={styles.sectionCount}>{verifiedEvidenceCount} verified evidence items</span>
          </div>
          <EvidenceList evidence={verifiedEvidence} variant="detailed" privacySafe />
        </section>

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Blockchain Documentation</h2>
          </div>
          <IPFSLinks 
            jsonCid={certification?.ipfsJsonCid || certification?.ipfs?.jsonCid} 
            pdfCid={certification?.ipfsPdfCid || certification?.ipfs?.pdfCid}
            jsonUrl={certification?.jsonUrl}
            pdfUrl={certification?.pdfUrl}
            finalJsonUrl={certification?.finalJsonUrl}
            finalPdfUrl={certification?.finalPdfUrl}
            ipfs={certification?.ipfs}
            ipfsGatewayUrls={certification?.ipfsGatewayUrls}
            gatewayExamples={certification?.gatewayExamples}
            variant="buttons" 
          />
          <p style={styles.helperText}>Certificate published on IPFS (decentralized storage)</p>
        </section>

        <footer style={styles.footer}>
          <div style={styles.footerCard}>
            <CertificationQRCode certificationId={certification?.certificationId} />
          </div>
          <div style={styles.footerCard}>
            <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a", marginBottom: 12 }}>Share Certificate</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              {/* X/Twitter */}
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=Check out this ${certification?.level || "ESG"} certified company: ${companyName}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  display: "inline-flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#000000",
                  color: "white",
                  textDecoration: "none",
                  fontSize: 20,
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                title="Share on X"
              >
                <FaXTwitter />
              </a>
              
              {/* LinkedIn */}
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  display: "inline-flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#0A66C2",
                  color: "white",
                  textDecoration: "none",
                  fontSize: 20,
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                title="Share on LinkedIn"
              >
                <FaLinkedin />
              </a>
              
              {/* Facebook */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  display: "inline-flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#1877F2",
                  color: "white",
                  textDecoration: "none",
                  fontSize: 20,
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                title="Share on Facebook"
              >
                <FaFacebook />
              </a>
              
              {/* WhatsApp */}
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Check out this ${certification?.level || "ESG"} certified company: ${companyName} - ${window.location.href}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  display: "inline-flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#25D366",
                  color: "white",
                  textDecoration: "none",
                  fontSize: 20,
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                title="Share on WhatsApp"
              >
                <FaWhatsapp />
              </a>
              
              {/* Email */}
              <a
                href={`mailto:?subject=Check out this ESG certified company&body=${encodeURIComponent(companyName + " - " + window.location.href)}`}
                style={{ 
                  display: "inline-flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#EA4335",
                  color: "white",
                  textDecoration: "none",
                  fontSize: 20,
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                title="Share via Email"
              >
                <FaEnvelope />
              </a>
              
              {/* Copy Link */}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href).then(() => {
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
                  }).catch(err => console.error('Copy failed:', err));
                }}
                style={{ 
                  display: "inline-flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: copySuccess ? "#10b981" : "#6366f1",
                  color: "white",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 18,
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                title={copySuccess ? "Copied!" : "Copy Link"}
              >
                {copySuccess ? "✓" : <FaLink />}
              </button>
              
              {/* Native Share (Web Share API) */}
              {navigator.share && (
                <button
                  onClick={() => {
                    navigator.share({
                      title: `${companyName} - ESG Certification`,
                      text: `Check out this ${certification?.level || "ESG"} certified company: ${companyName}`,
                      url: window.location.href
                    }).catch(err => {
                      if (err.name !== 'AbortError') {
                        console.error('Share failed:', err);
                      }
                    });
                  }}
                  style={{ 
                    display: "inline-flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "#8b5cf6",
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 20,
                    transition: "all 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                  title="Share..."
                >
                  <FaShareNodes />
                </button>
              )}
            </div>
          </div>
          <div style={styles.footerNote}>Publicly verifiable certificate</div>
        </footer>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f8fafc",
    minHeight: "100vh",
    padding: "40px 16px",
  },
  container: {
    maxWidth: "1100px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },
  card: {
    background: "#ffffff",
    padding: "24px",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
    textAlign: "center",
  },
  // AppleTree Branding (Certificatore)
  appleTreeHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px 20px",
    background: "linear-gradient(135deg, #7CFFB2 0%, #48BB78 100%)",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(124, 255, 178, 0.3)",
  },
  appleTreeLogo: {
    width: 40,
    height: 40,
    objectFit: "contain",
  },
  appleTreeText: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#065f46",
    letterSpacing: "0.5px",
  },
  // Certified Company Section
  companySection: {
    display: "flex",
    gap: "24px",
    alignItems: "center",
    background: "#ffffff",
    borderRadius: "16px",
    padding: "32px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
  },
  companyLogoBox: {
    width: 96,
    height: 96,
    borderRadius: 16,
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    border: "2px solid #e2e8f0",
  },
  companyLogo: {
    width: 80,
    height: 80,
    objectFit: "contain",
  },
  companyLogoPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "2.5rem",
    fontWeight: 700,
    color: "#94a3b8",
    background: "linear-gradient(135deg, #e0e7ff 0%, #dbeafe 100%)",
    borderRadius: 14,
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: "2rem",
    margin: 0,
    color: "#0f172a",
    fontWeight: 700,
  },
  companySubtitle: {
    margin: "8px 0 0 0",
    color: "#64748b",
    fontSize: "1.1rem",
  },
  hero: {
    display: "flex",
    gap: "24px",
    alignItems: "center",
    background: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
  },
  heroTitle: {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: 8,
  },
  heroDates: {
    marginTop: 12,
    color: "#475569",
  },
  statusBadge: {
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "0.85rem",
    fontWeight: 700,
  },
  section: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    gap: 16,
    flexWrap: "wrap",
  },
  sectionTitle: {
    fontSize: "1.3rem",
    margin: 0,
  },
  sectionCount: {
    color: "#64748b",
    fontWeight: 600,
  },
  helperText: {
    marginTop: 12,
    color: "#64748b",
  },
  footer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
  },
  footerCard: {
    background: "#ffffff",
    borderRadius: "12px",
    padding: "16px",
    textAlign: "center",
    color: "#475569",
    boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
  },
  footerNote: {
    gridColumn: "1 / -1",
    textAlign: "center",
    color: "#94a3b8",
  },
};

export default PublicCertificationPage;
